// AIDEV-NOTE: dropbox-sign-refactored; Production Dropbox Sign service implementation

import { BaseThirdPartyService, ServiceError } from '../base-service'
import type {
  ISignatureService,
  SignatureSessionData,
  SignatureStatus,
} from '../types'

const DROPBOX_SIGN_API_BASE = 'https://api.hellosign.com/v3'

export class DropboxSignService
  extends BaseThirdPartyService
  implements ISignatureService
{
  private isDevelopment: boolean
  private apiKey?: string

  constructor(apiBase: string = '/api') {
    super({
      apiBase,
      timeout: 30000,
    })

    // Check for direct API access in development
    this.isDevelopment = !!import.meta.env.VITE_DROPBOX_SIGN_API_KEY
    this.apiKey = import.meta.env.VITE_DROPBOX_SIGN_API_KEY
  }

  // AIDEV-NOTE: dropbox-sign-integration; createSignatureSession removed
  // Signature sessions are now created by the backend during pre-reservation
  // The frontend receives the session data (request ID and claim URL) in the pre-reservation response

  async getSignatureSession(sessionId: string): Promise<SignatureSessionData> {
    // Development: Direct API call
    if (this.isDevelopment && this.apiKey) {
      return this.getSignatureSessionDirect(sessionId)
    }

    // Production: Call backend API
    return this.fetchApi<SignatureSessionData>(
      `/dropbox-sign/signature-sessions/${sessionId}`
    )
  }

  private async getSignatureSessionDirect(
    sessionId: string
  ): Promise<SignatureSessionData> {
    const response = await fetch(
      `${DROPBOX_SIGN_API_BASE}/signature_request/${sessionId}`,
      {
        headers: {
          Authorization: `Basic ${btoa(this.apiKey + ':')}`,
        },
      }
    )

    if (!response.ok) {
      throw new ServiceError(
        'Failed to fetch signature session',
        response.status
      )
    }

    const data = await response.json()
    return this.mapDropboxSignResponse(data.signature_request, sessionId)
  }

  async getEmbeddedSignUrl(signatureId: string): Promise<string> {
    // Development: Direct API call
    if (this.isDevelopment && this.apiKey) {
      return this.getEmbeddedSignUrlDirect(signatureId)
    }

    // Production: Call backend API
    const response = await this.fetchApi<{ signUrl: string }>(
      `/dropbox-sign/signature-sessions/${signatureId}/sign-url`
    )
    return response.signUrl
  }

  private async getEmbeddedSignUrlDirect(signatureId: string): Promise<string> {
    const response = await fetch(
      `${DROPBOX_SIGN_API_BASE}/embedded/sign_url/${signatureId}`,
      {
        method: 'GET',
        headers: {
          Authorization: `Basic ${btoa(this.apiKey + ':')}`,
        },
      }
    )

    if (!response.ok) {
      throw new ServiceError('Failed to get embedded sign URL', response.status)
    }

    const data = await response.json()
    return data.embedded.sign_url
  }

  private mapDropboxSignResponse(
    signatureRequest: {
      signature_request_id: string
      test_mode: boolean
      is_complete?: boolean
      has_error?: boolean
      final_copy_uri?: string
      signatures: Array<{
        signature_id: string
        signer_role: string
        signer_email_address: string
        signer_name: string
        signed_at?: string
      }>
    },
    sessionId: string
  ): SignatureSessionData {
    let status: SignatureStatus
    if (signatureRequest.is_complete) {
      status = 'completed'
    } else if (signatureRequest.has_error) {
      status = 'failed'
    } else {
      status = 'pending'
    }

    const signatures = signatureRequest.signatures || []

    return {
      signatureSessionId: sessionId,
      claimUrl: `https://app.hellosign.com/sign/${sessionId}`,
      status,
      signatureId: signatures[0]?.signature_id,
      documentUrl: signatureRequest.final_copy_uri,
      signedAt: signatures[0]?.signed_at,
    }
  }
}
